import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash, Plus } from "@phosphor-icons/react";

// Reusable line-item editor (used by Budget, Cash/Check Vouchers, Quotation, PO).
// Operational Requests use a dedicated OperationalCategoryEditor instead of this
// free-form editor.
export function LineItemsEditor({ items, onChange }) {
  function updateItem(i, patch) {
    const next = items.map((it, idx) => idx === i ? { ...it, ...patch } : it);
    onChange(next);
  }
  function add() {
    onChange([...(items || []), { description: "", quantity: 1, unit_price: 0 }]);
  }
  function remove(i) {
    onChange(items.filter((_, idx) => idx !== i));
  }
  const total = (items || []).reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Line Items</Label>
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="add-line-item-btn" className="border-border">
          <Plus size={14} className="mr-1" /> Add item
        </Button>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <div className="grid grid-cols-[1fr_90px_120px_120px_40px] gap-2 px-3 py-2 bg-muted text-[11px] uppercase tracking-widest text-muted-foreground">
          <div>Description</div><div>Qty</div><div>Unit price</div><div className="text-right">Line total</div><div />
        </div>
        {(items || []).length === 0 ? (
          <div className="px-3 py-4 text-sm text-muted-foreground">No items yet. Click “Add item”.</div>
        ) : items.map((it, i) => (
          <div key={i} className="grid grid-cols-[1fr_90px_120px_120px_40px] gap-2 px-3 py-2 border-t border-border items-center">
            <Input
              value={it.description}
              onChange={(e) => updateItem(i, { description: e.target.value })}
              placeholder="e.g. Laptop Dell XPS 13"
              className="h-8 border-border"
              data-testid={`line-item-desc-${i}`}
            />
            <Input
              type="number" min="0" step="1" value={it.quantity}
              onChange={(e) => updateItem(i, { quantity: Number(e.target.value) })}
              className="h-8 tabular-nums border-border" data-testid={`line-item-qty-${i}`}
            />
            <Input
              type="number" min="0" step="0.01" value={it.unit_price}
              onChange={(e) => updateItem(i, { unit_price: Number(e.target.value) })}
              className="h-8 tabular-nums border-border" data-testid={`line-item-unit-${i}`}
            />
            <div className="text-right tabular-nums text-sm">
              {((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)).toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`line-item-remove-${i}`} className="h-8 w-8">
              <Trash size={14} />
            </Button>
          </div>
        ))}
        <div className="grid grid-cols-[1fr_90px_120px_120px_40px] gap-2 px-3 py-2 border-t border-border bg-muted/40">
          <div className="col-start-4 text-right font-heading font-bold tabular-nums" data-testid="line-items-total">
            {total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>
    </div>
  );
}

export function computeLineTotal(items) {
  return (items || []).reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
}
